"use client"
import { Box, Typography, Button, Card, CardContent, Container } from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import LockIcon from "@mui/icons-material/Lock"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ContactSupportIcon from "@mui/icons-material/ContactSupport"
import HomeIcon from "@mui/icons-material/Home"
import PropTypes from "prop-types"

// Tema simple y limpio
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    error: {
      main: "#dc3545",
    },
    background: {
      default: "#f8f9fa",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 20px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      },
    },
  },
})

const NoTienesAccesosComponent = ({
  titulo = "Acceso Denegado",
  mensaje = "No tienes los permisos necesarios para acceder a esta sección. Contacta con el administrador del sistema si necesitas acceso.",
  onVolver,
  onContactarSoporte,
  onIrInicio,
  mostrarBotones = {
    volver: true,
    contactar: true,
    inicio: true,
  },
}) => {
  const handleVolver = () => {
    if (onVolver) {
      onVolver()
    } else {
      window.history.back()
    }
  }

  const handleContactar = () => {
    if (onContactarSoporte) {
      onContactarSoporte()
    } else {
      window.location.href = "mailto:soporte@empresa.com?subject=Solicitud de Acceso"
    }
  }

  const handleIrInicio = () => {
    if (onIrInicio) {
      onIrInicio()
    } else {
      window.location.href = "/"
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 3,
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={2}>
            <CardContent sx={{ p: 5, textAlign: "center" }}>
              {/* Icono principal */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "#fff5f5",
                  border: "2px solid #fed7d7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px auto",
                }}
              >
                <LockIcon
                  sx={{
                    fontSize: 40,
                    color: theme.palette.error.main,
                  }}
                />
              </Box>

              {/* Código de error */}
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  mb: 1,
                  fontSize: "0.9rem",
                  letterSpacing: 1,
                }}
              >
                ERROR 403
              </Typography>

              {/* Título */}
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              >
                {titulo}
              </Typography>

              {/* Mensaje */}
              <Typography
                variant="body1"
                sx={{
                  color: "#6c757d",
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                {mensaje}
              </Typography>

              {/* Información adicional */}
              <Box
                sx={{
                  backgroundColor: "#e3f2fd",
                  border: "1px solid #bbdefb",
                  borderRadius: 2,
                  p: 2.5,
                  mb: 4,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  💡 ¿Necesitas acceso?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#5a6c7d",
                  }}
                >
                  Contacta con tu administrador para solicitar los permisos necesarios.
                </Typography>
              </Box>

              {/* Botones de acción */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {mostrarBotones.volver && (
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleVolver}
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Volver
                  </Button>
                )}

                {mostrarBotones.contactar && (
                  <Button
                    variant="contained"
                    startIcon={<ContactSupportIcon />}
                    onClick={handleContactar}
                    sx={{
                      backgroundColor: theme.palette.secondary.main,
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#e6a00d",
                      },
                    }}
                  >
                    Contactar Soporte
                  </Button>
                )}

                {mostrarBotones.inicio && (
                  <Button
                    variant="outlined"
                    startIcon={<HomeIcon />}
                    onClick={handleIrInicio}
                    sx={{
                      borderColor: "#6c757d",
                      color: "#6c757d",
                    }}
                  >
                    Inicio
                  </Button>
                )}
              </Box>

              {/* Footer simple */}
              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e9ecef" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#adb5bd",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Si el problema persiste, contacta con soporte técnico
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#adb5bd",
                    fontFamily: "monospace",
                    backgroundColor: "#f8f9fa",
                    padding: "4px 8px",
                    borderRadius: 1,
                    border: "1px solid #e9ecef",
                  }}
                >
                  Ref: {new Date().getTime().toString().slice(-6)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  )
}

NoTienesAccesosComponent.propTypes = {
  titulo: PropTypes.string,
  mensaje: PropTypes.string,
  onVolver: PropTypes.func,
  onContactarSoporte: PropTypes.func,
  onIrInicio: PropTypes.func,
  mostrarBotones: PropTypes.shape({
    volver: PropTypes.bool,
    contactar: PropTypes.bool,
    inicio: PropTypes.bool,
  }),
}

export default NoTienesAccesosComponent
